import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { getPoints } from '@/api/catalog.api'
import { getExcursions } from '@/api/excursion.api'
import { getCategories } from '@/api/excursions-categories.api'
import { catalogTypeDetail, InterestPoint } from '@/core/interface/Catalog'
import { Excursion } from '@/core/interface/Excursion'
import { ExcursionCategory } from '@/core/interface/ExcursionCategory'

interface ICatalogState {
  catalogType: catalogTypeDetail
  interestPoints: InterestPoint[]
  selectedInterestPoint: InterestPoint | null
  selectedTab: 'allPins' | 'top' | 'mustSee'
  categories: string[]
  selectedTags: string[]
  isFilterChanged: boolean
  playedAudioIds: string[]
  isPayedClient: boolean
  excursions: Excursion[]
  excursionCategories: ExcursionCategory[]
  payedTours: string[]
  gotTutorial: boolean
}

export const DEFAULT_TAGS = [
  'building',
  'bridge',
  'museum',
  'monument',
  'church',
  'street',
  'plaza',
  'BAR',
  'REST',
  'CAFE',
  'ICE-CR',
  'TERR',
  'other',
]

const ExcursionCategoriesValues = [
  { id: 'my', name: 'MY' },
  { id: 'highlights', name: 'HIGHLIGHTS' },
  { id: 'thematic', name: 'THEMATIC' },
  { id: 'exclusive', name: 'EXCLUSIVE' },
]

const initialState: ICatalogState = {
  catalogType: 'short',
  interestPoints: [],
  selectedInterestPoint: null,
  selectedTab: 'allPins',
  categories: [],
  selectedTags: DEFAULT_TAGS,
  isFilterChanged: false,
  playedAudioIds: [],
  isPayedClient: false,
  payedTours: [],
  gotTutorial: false,
  excursions: [],
  excursionCategories: ExcursionCategoriesValues,
}

export const getPointsThunk = createAsyncThunk('getPoints', async () => await getPoints())

export const getExcursionPoints = createAsyncThunk('getExcursionPoints', async () => {
  const data = await getExcursions()

  return data
})

export const getExcursionCategories = createAsyncThunk('getExcursionCategories', async () => {
  const data = await getCategories('en')

  return data
})

export const forecastState = createSlice({
  name: 'forecast',
  initialState,
  reducers: {
    setInterestPoints(state, action) {
      state.interestPoints = action.payload
    },
    setSelectedInterestPoint(state, action) {
      state.selectedInterestPoint = action.payload
    },
    setCatalogType(state, action: PayloadAction<catalogTypeDetail>) {
      state.catalogType = action.payload
    },
    setSelectedCategories(state, action) {
      // if (typeof action.payload.category === 'number' && Array.isArray(action.payload.included)) {
      state.selectedTags = action.payload
      // } else {
      //   console.error('Categories set in the old way!')
      // }
    },
    setSelectedTab(state, action: PayloadAction<{ type: ICatalogState['selectedTab'] }>) {
      state.selectedTab = action.payload.type
    },
    setFilterChanged(state, action: PayloadAction<boolean>) {
      state.isFilterChanged = action.payload
    },
    addPlayedAudio(state, action: PayloadAction<string>) {
      // Clone the state.playedAudioIds array to avoid mutating the original state
      const tmpIds = [...state.playedAudioIds]

      // Check if action.payload exists in playedAudioIds
      const index = tmpIds.indexOf(action.payload)

      if (index !== -1) {
        // If action.payload exists, remove it from its current position
        tmpIds.splice(index, 1)
      }

      // Add action.payload to the end of the array
      tmpIds.push(action.payload)

      state.playedAudioIds = tmpIds
    },
    setPayed(state, action: PayloadAction<boolean>) {
      state.isPayedClient = action.payload
    },
    setPayedTours(state, action: PayloadAction<string[]>) {
      state.payedTours = action.payload
    },
    setGotTutorial(state, action: PayloadAction<boolean>) {
      state.gotTutorial = action.payload
    },
    setExcursions(state, action: PayloadAction<Excursion[]>) {
      state.excursions = action.payload
    },
  },
  extraReducers: (builder) => {
    //getPoints is defined higher
    builder.addCase(
      getPointsThunk.fulfilled,
      (state, action: PayloadAction<InterestPoint[] | undefined>) => {
        if (action.payload && action.payload.length) {
          state.interestPoints = [...action.payload]

          const allCategories = action.payload?.reduce((acc, point) => {
            acc.push(point.type)
            return acc
          }, [] as string[])

          const uniqueCategories = Array.from(new Set(allCategories))

          state.categories = [...uniqueCategories]
        }
      },
    )
    builder.addCase(
      getExcursionPoints.fulfilled,
      (state, action: PayloadAction<Excursion[] | undefined>) => {
        if (action.payload && action.payload.length) {
          state.excursions = [...action.payload]
        }
      },
    )
    builder.addCase(
      getExcursionCategories.fulfilled,
      (state, action: PayloadAction<ExcursionCategory[]>) => {
        if (action.payload && action.payload.length) {
          state.excursionCategories = [...action.payload]
        }
      },
    )
  },
})

export const {
  setInterestPoints,
  setSelectedInterestPoint,
  setCatalogType,
  setSelectedTab,
  setSelectedCategories,
  setFilterChanged,
  addPlayedAudio,
  setPayed,
  setPayedTours,
  setGotTutorial,
  setExcursions,
} = forecastState.actions

export default forecastState.reducer
